import { yupResolver } from '@hookform/resolvers/yup';
import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
// @mui
import LoadingButton from '@mui/lab/LoadingButton';
import { Box, Button } from '@mui/material';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
// routes
import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';
// hooks
import { useBoolean } from 'src/hooks/use-boolean';
// components
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import FormProvider, { RHFTextField } from 'src/components/hook-form';
import { useLoginMutation, useSocialLoginMutation } from "../../../store/Reducer/auth";
import axios from 'axios';
import { selectDeviceId, setUser } from 'src/store/slices/userSlice';
import { useMockedUser } from 'src/hooks/use-mocked-user';
import { jwtDecode } from 'jwt-decode';
import { LoadingScreen } from 'src/components/loading-screen';
import { Link } from 'react-router-dom';


export default function JwtLoginView({ checked }) {

  const [userTimeZone, setUserTimeZone] = useState('');
   const [isLoading, setIsLoading] = useState(false);

  const [loginData] = useLoginMutation();
  const [socialLoginData] = useSocialLoginMutation();

  const router = useRouter();
  const dispatch = useDispatch();

  const deviceId = useSelector(selectDeviceId);
  const { user } = useMockedUser();

  useEffect(() => {
    if (user) {
      if (user.user_type === '2') {
        router.replace(paths.adminDashboard.root);
      } else if (user.user_type === '3' || user.user_type === '4') {
        router.replace(paths.dashboard.file);
      } else if (user.user_type === '1') {
        router.replace(paths.superAdminDashboard.root);
      }
    }
  }, [user])


  useEffect(() => {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    setUserTimeZone(zone);
    const hash = window.location.hash;
    if (hash && hash.includes('access_token=')) {
      handleMicrosoftRedirect(hash,zone);
    }
  }, []);

  const LoginSchema = Yup.object().shape({
    email: Yup.string()
      .required('Email is required')
      .email('Email must be a valid email address'),
  });

  const defaultValues = {
    email: '',
  };

  const methods = useForm({
    resolver: yupResolver(LoginSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = handleSubmit(async (data) => {
    try {
      const payload = {
        email: data.email,
        device_type: 'web',
        device_id: deviceId || 'abc',
        timezone: userTimeZone,
      };
      const response = await loginData(payload);
      if (response.error) {
        return toast.error(response?.error?.data?.message);
      } else if (response?.data?.status === 500) {
        router.replace(`${paths.auth.jwt.loginPassword}?email=${encodeURIComponent(data?.email)}`);
      } else if (response?.data?.status === 401) {
        toast.error(response?.data?.message)
        // router.replace(`${paths.auth.jwt.loginNewPassword}?email=${encodeURIComponent(data?.email)}`);
      } else {
        toast.error('User Not Found');
      }
    } catch (error) {
      console.error(error);
      toast.error(error?.data?.message || 'Failed to login');
    }
  });


  const handleMicrosoftLogin = () => {
    const clientId = 'aff8cd11-44d8-4767-8c3b-e9f869b1957f';
    const redirectUri ="https://www.sitesnapbyisat.com";
    const scope = 'https://graph.microsoft.com/user.read';
    const authUrl = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=${clientId}&response_type=token&redirect_uri=${redirectUri}&scope=${scope}&prompt=select_account`;
    window.location.href = authUrl;
  };

  const handleMicrosoftRedirect = async (hash,userTimeZone) => {
    setIsLoading(true);
    const params = new URLSearchParams(hash.replace('#', '?'));
    const accessToken = params.get('access_token');
    if (!accessToken) return;
    try {
      const userData = await fetch("https://graph.microsoft.com/v1.0/me", {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }).then(res => res.json());
    if (!userData || !userData?.mail) {
      toast.error("Failed to fetch Microsoft user info");
      return;
    }
      const payload = {
        email: userData?.mail,
        device_type: 'web', 
        device_id: deviceId || 'abc',
        timezone: userTimeZone,
        social_id:userData?.id,
        social_type:"microsoft"
      };
      const response = await socialLoginData(payload).unwrap();
      if (!response?.error) {
        const user = response.data;
        dispatch(setUser(user));
        if (user.user_type === '2') {
          router.replace(paths.adminDashboard.root);
        } else if (user.user_type === '3' || user.user_type === '4') {
          router.replace(paths.dashboard.file);
        } else if (user.user_type === '1') {
          router.replace(paths.superAdminDashboard.root);
        }
      } 
    } catch (err) {
      setIsLoading(false);
      toast.error('Microsoft login failed');
    } finally {
       setIsLoading(false);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  };

  if(isLoading){return <LoadingScreen />}
  
   return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      <Stack spacing={1} sx={{ mb: 3 }}>
        <Typography variant="h4">Sign in to SiteSnap</Typography>
        <Typography variant="body2" color="text.secondary">
          Let's get you signed in to continue.
        </Typography>
      </Stack>

      <Stack spacing={2.5}>
        <RHFTextField 
          name="email" 
          label="Email address" 
          placeholder="your@email.com"
        />

        <LoadingButton
          fullWidth
          size="large"
          type="submit"
          variant="contained"
          disabled={!checked}
          loading={isSubmitting}
          sx={{
            bgcolor: '#de4d1b',
            '&:hover': {
              bgcolor: '#c4411a',
            },
          }}
        >
          Continue
        </LoadingButton>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Box
            sx={{ flexGrow: 1, border: 'solid 1px', borderColor: 'divider' }}
          />
          <Typography variant="body2" sx={{ mx: 1, color: 'text.secondary' }}>
            Or continue with
          </Typography>
          <Box
            sx={{ flexGrow: 1, border: 'solid 1px', borderColor: 'divider' }}
          />
        </Box>

        <Button
          variant="outlined"
          sx={{ color: 'text.disabled' }}
          size="large"
          disabled={!checked}
          onClick={handleMicrosoftLogin}
          startIcon={
            <img
              src={`${process.env.PUBLIC_URL}/assets/microsoft-logo.png`}
              alt="microsoft"
            />
          }
        >
          Microsoft
        </Button>
      </Stack>
     
      <Stack spacing={1} sx={{ mb: 3 , mt: 2 }} alignItems="center">
        <Stack direction="row" spacing={0.5} justifyContent="center">
        <Typography variant="caption">Don't have an account?</Typography>
        <Link  to={paths.auth.jwt.register} style={{ fontSize: '0.75rem', color: '#de4d1b', textDecoration: 'none' }}>
          Create new account
        </Link>
      </Stack>
      </Stack>

    </FormProvider>
  );
}
